<template>
  <div class="image-label">
    <div class="image-content">
      <el-radio-group
        v-model="drawMode"
        size="large"
        fill="#409eff"
        @change="onSetMode"
      >
        <el-radio-button value="PAN">
          <el-tooltip content="鼠标" placement="top">
            <el-icon><Pointer /></el-icon>
          </el-tooltip>
        </el-radio-button>
        <el-radio-button value="POLYGON" v-if="labelValue">
          <el-tooltip content="图像分割" placement="top">
            <el-icon><Edit /></el-icon>
          </el-tooltip>
        </el-radio-button>
      </el-radio-group>
      <div
        v-loading.fullscreen.lock="loading"
        element-loading-text="加载中......"
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(0, 0, 0, 0.1)"
        id="AILabel-container"
        class=""
      >
        <div
          ref="popoverRef"
          class="popover-anchor"
          :style="{ left: menuX + 'px', top: menuY + 'px' }"
        />
      </div>

      <el-popover
        v-model:visible="popoverVisible"
        :virtual-ref="popoverRef"
        virtual-triggering
        trigger="manual"
        placement="right-start"
        width="140"
        :show-arrow="false"
      >
        <div class="menu-item danger" @click="deleteLabel">删除</div>
      </el-popover>
    </div>
    <LabelSelect ref="LabelSelectRef" @change="labelChange" />
  </div>
</template>

<script setup>
import AILabel from "ailabel";
import {
  ref,
  defineEmits,
  defineExpose,
  nextTick,
  getCurrentInstance,
} from "vue";
import { Pointer, Edit } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import LabelSelect from "./LabelSelect.vue";

/* ================= 基础状态 ================= */
const emit = defineEmits(["change"]);
const { proxy } = getCurrentInstance();

const loading = ref(false);
const fileUrl = ref("");
const imageBlob = ref("");

const imageW = ref(0);
const imageH = ref(0);

const ailabelRef = ref(null);
const imageLayer = ref(null);
const featureLayer = ref(null);
const textLayer = ref(null);

/* ================= Label ================= */
const labelValue = ref(null);
const labelChange = (val) => {
  labelValue.value = val;
};

/* ================= 右键菜单 ================= */
const popoverRef = ref();
const popoverVisible = ref(false);
const clickRegion = ref(null);
const menuX = ref(0);
const menuY = ref(0);

/* ================= 绘制模式 ================= */
const drawMode = ref("PAN");
const onSetMode = (mode) => {
  drawMode.value = mode;
  ailabelRef.value?.setMode(mode);
};

/* ================= 标注数据 ================= */
const labelList = ref([]);

/* ================= 核心补丁状态 ================= */
// drawDone 后忽略 click
let lastDrawTime = 0;
const IGNORE_CLICK_AFTER_DRAW = 300;

// 全局唯一 ID
let uidSeed = Date.now();
const genUID = () => `poly-${uidSeed++}`;

/* ================= open ================= */
const open = async (question, answer) => {
  try {
    loading.value = true;
    fileUrl.value = question.url;
    const labelOptions = JSON.parse(question.problem);
    proxy.$refs.LabelSelectRef.setOptions(labelOptions);

    await destroyAILabel();
    await loadImage();
    await nextTick();
    await ailabelInit();

    if (Array.isArray(answer)) {
      for (const item of answer) {
        const labelData = labelOptions.find((i) => i.id == item.cls);
        if (!labelData) continue;

        const points = [];
        for (let i = 0; i < item.segmentation[0].length; i += 2) {
          points.push({
            x: item.segmentation[0][i],
            y: item.segmentation[0][i + 1],
          });
        }
        drawPolygon(points, labelData, true);
      }
    }
  } finally {
    loading.value = false;
    labelValue.value = null;
  }
};

/* ================= 销毁 ================= */
const destroyAILabel = async () => {
  if (ailabelRef.value) {
    ailabelRef.value.destroy();
    ailabelRef.value = null;
  }
  labelList.value = [];
  popoverVisible.value = false;
};

/* ================= 加载图片 ================= */
const loadImage = () =>
  new Promise(async (resolve, reject) => {
    try {
      const res = await fetch(fileUrl.value);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      imageBlob.value = url;

      const img = new Image();
      img.onload = () => {
        imageW.value = img.width;
        imageH.value = img.height;
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });

/* ================= 初始化 ================= */
const ailabelInit = async () => {
  const container = document.getElementById("AILabel-container");
  if (!container) return;

  const cw = container.clientWidth || 900;
  const ch = container.clientHeight || 600;
  const scale = Math.min(cw / imageW.value, ch / imageH.value);
  const zoom = (cw / scale) * 0.9;

  const map = new AILabel.Map("AILabel-container", {
    center: { x: imageW.value / 2, y: imageH.value / 2 },
    zoom,
    zoomWhenDrawing: true,
    panWhenDrawing: true,
    withHotKeys: true,
  });

  ailabelRef.value = map;

  map.setDrawingStyle({
    strokeStyle: "#409EFF",
    fillStyle: "#409EFF",
    lineWidth: 4,
  });

  initImageLayer();
  initFeatureLayer();
  initTextLayer();
  initEvents();
};

/* ================= 图层 ================= */
const initImageLayer = () => {
  imageLayer.value = new AILabel.Layer.Image(
    "image-layer",
    {
      src: imageBlob.value,
      width: imageW.value,
      height: imageH.value,
      position: { x: 0, y: 0 },
    },
    {},
    { zIndex: 5 },
  );
  ailabelRef.value.addLayer(imageLayer.value);
};

const initFeatureLayer = () => {
  featureLayer.value = new AILabel.Layer.Feature(
    "feature-layer",
    {},
    { zIndex: 10, opacity: 0.5 },
  );
  ailabelRef.value.addLayer(featureLayer.value);
};

const initTextLayer = () => {
  textLayer.value = new AILabel.Layer.Text("text-layer", {}, { zIndex: 12 });
  ailabelRef.value.addLayer(textLayer.value);
};

/* ================= 事件 ================= */
const initEvents = () => {
  ailabelRef.value.events.on("drawDone", (type, data) => {
    lastDrawTime = Date.now();

    if (!labelValue.value) {
      ElMessage.warning("请先选择标签");
      return;
    }
    drawPolygon(data, labelValue.value);
  });

  ailabelRef.value.events.on("click", (e) => {
    if (drawMode.value !== "PAN") return;
    if (Date.now() - lastDrawTime < IGNORE_CLICK_AFTER_DRAW) return;

    const hit = hitTest(e.global);
    if (!hit) return;

    // popoverVisible.value = false;

    clickRegion.value = hit;
    menuX.value = e.screen.x;
    menuY.value = e.screen.y;
    setTimeout(() => {
      popoverVisible.value = true;
    }, 200);
  });
};

/* ================= 删除 ================= */
const deleteLabel = () => {
  const id = clickRegion.value?.id;
  if (!id) return;

  featureLayer.value.removeFeatureById(id);
  textLayer.value.removeTextById(id);
  labelList.value = labelList.value.filter((i) => i.id !== id);

  emitAnswer();
  popoverVisible.value = false;
  ElMessage.success("删除成功");
};

/* ================= 计算中心 ================= */
const getPolygonCentroid = (pts) => {
  let area = 0,
    cx = 0,
    cy = 0;

  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const cross = pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    area += cross;
    cx += (pts[i].x + pts[j].x) * cross;
    cy += (pts[i].y + pts[j].y) * cross;
  }
  area *= 0.5;
  return { x: cx / (6 * area), y: cy / (6 * area) };
};

/* ================= 绘制 Polygon ================= */
const drawPolygon = (shape, form, isRestore = false) => {
  const id = genUID();

  const polygon = new AILabel.Feature.Polygon(id, { points: shape }, form, {
    strokeStyle: form.color,
    fillStyle: form.color,
    globalAlpha: 0.2,
    stroke: true,
    fill: true,
  });

  featureLayer.value.addFeature(polygon);

  const center = getPolygonCentroid(shape);
  const text = new AILabel.Text(
    id,
    {
      text: form.label,
      position: center,
      offset: { x: -form.label.length * 10, y: -26 },
    },
    form,
    {
      fillStyle: form.color,
      background: true,
      fontColor: "#ffffff",
      strokeStyle: "transparent",
      font: "normal 20px Arial",
    },
  );

  textLayer.value.addText(text);

  labelList.value.push({ id, shape, props: form });

  if (!isRestore && !loading.value) {
    emitAnswer();
    onSetMode("PAN");
  }
};

const emitAnswer = () => {
  emit(
    "change",
    labelList.value.map((i) => ({
      cls: i.props.id,
      segmentation: [i.shape.flatMap((p) => [p.x, p.y])],
    })),
  );
};

/* ================= 命中 ================= */
const isPointInPolygon = (point, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const hitTest = (point) => {
  for (const item of [...labelList.value].reverse()) {
    if (isPointInPolygon(point, item.shape)) return item;
  }
  return null;
};

defineExpose({ open });
</script>

<style scoped lang="less">
.image-label {
  display: flex;
  height: 600px;
  justify-content: space-between;
  align-items: flex-start;
  .image-content {
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}
#AILabel-container {
  margin-top: 12px;
  background-color: rgb(177, 179, 184);
  width: 100%;
  height: calc(100% - 100px);
  position: relative;
  overflow: hidden;
}
.popover-anchor {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}

.menu-item {
  cursor: pointer;
}

.menu-item.danger {
  color: #ef4444;
}

.menu-item:hover {
  background: #f3f4f6;
}

.label-list {
  padding: 20px;
  color: black;
  .label-item {
    padding: 8px;
    margin-bottom: 8px;
    cursor: pointer;
    border-radius: 4px;
    position: relative;
    color: #fff;
    &.is-active {
      &::after {
        content: "";
        position: absolute;
        left: -20px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-top: 12px solid transparent;
        border-bottom: 12px solid transparent;
        border-left: 12px solid #409eff;
      }
    }
  }
}
</style>
